import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DBButton from "../../component/global/DBButton";
import DBModal from "../../component/global/DBModal";
import { errorMessage, incentivesApi } from "./api";
import { formatNumber } from "./helpers";

export default function CoinPurchaseModal({
    show,
    onHide,
    onPurchased,
    stacked,
    initialQuantity,
    note,
}) {
    const { t, i18n } = useTranslation();
    const [quantity, setQuantity] = useState(initialQuantity || 1000);
    const [unitRate, setUnitRate] = useState(100);
    const [paymentMethod, setPaymentMethod] = useState("wallet");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const total = useMemo(
        () => Math.max(0, Number(quantity) || 0) * (Number(unitRate) || 0),
        [quantity, unitRate]
    );

    useEffect(() => {
        if (show) {
            setQuantity(Number(initialQuantity) > 0 ? Number(initialQuantity) : 1000);
            setError("");
        }
    }, [show, initialQuantity]);

    useEffect(() => {
        if (!show || Number(quantity) < 1) return;
        const timer = setTimeout(() => {
            incentivesApi
                .quoteCoins(Number(quantity))
                .then((quote) =>
                    setUnitRate(
                        quote.unit_rate_toman || quote.unit_rate || unitRate
                    )
                )
                .catch(() => {});
        }, 300);
        return () => clearTimeout(timer);
    }, [show, quantity]); // eslint-disable-line react-hooks/exhaustive-deps

    const purchase = () => {
        if (Number(quantity) < 1) {
            setError(t("incentives.purchase.quantityError"));
            return;
        }
        setLoading(true);
        setError("");
        incentivesApi
            .purchaseCoins({
                quantity: Number(quantity),
                method: paymentMethod,
                idempotency_key:
                    window.crypto?.randomUUID?.() ||
                    `purchase-${Date.now()}-${Math.random()
                        .toString(36)
                        .slice(2)}`,
                callback_url: window.location.href.split("?")[0],
            })
            .then((purchaseResult) => {
                if (purchaseResult.payment_url) {
                    window.location.assign(purchaseResult.payment_url);
                    return;
                }
                onPurchased?.(purchaseResult);
                onHide?.();
            })
            .catch((requestError) =>
                setError(
                    errorMessage(requestError, t("incentives.purchase.error"))
                )
            )
            .finally(() => setLoading(false));
    };

    const locale = i18n.language === "fa" ? "fa-IR" : "en-US";
    return (
        <DBModal
            show={show}
            onHide={onHide}
            stacked={stacked}
            minWidth={480}
            className="coin-purchase-modal"
            ariaLabelledby="coin-purchase-title"
        >
            <DBModal.Header
                closeBtn={onHide}
                title={
                    <span>{t("incentives.purchase.title")}</span>
                }
            />
            <DBModal.Content>
                <div className="coin-purchase-form">
                    {note ? (
                        <p className="coin-purchase-resume">{note}</p>
                    ) : null}
                    <label>
                        <span>{t("incentives.purchase.quantity")}</span>
                        <input
                            autoFocus
                            type="number"
                            min="1"
                            inputMode="numeric"
                            value={quantity}
                            onChange={(e) => setQuantity(e.target.value)}
                        />
                    </label>
                    <dl aria-live="polite">
                        <div>
                            <dt>{t("incentives.purchase.unitRate")}</dt>
                            <dd>
                                {formatNumber(unitRate, locale)}{" "}
                                {t("incentives.toman")}
                            </dd>
                        </div>
                        <div>
                            <dt>{t("incentives.purchase.total")}</dt>
                            <dd>
                                {formatNumber(total, locale)}{" "}
                                {t("incentives.toman")}
                            </dd>
                        </div>
                    </dl>
                    <fieldset>
                        <legend>{t("incentives.purchase.payment")}</legend>
                        <label>
                            <input
                                type="radio"
                                name="payment-method"
                                checked={paymentMethod === "wallet"}
                                onChange={() => setPaymentMethod("wallet")}
                            />
                            {t("incentives.purchase.wallet")}
                        </label>
                        <label>
                            <input
                                type="radio"
                                name="payment-method"
                                checked={paymentMethod === "zarinpal"}
                                onChange={() => setPaymentMethod("zarinpal")}
                            />
                            {t("incentives.purchase.zarinpal")}
                        </label>
                    </fieldset>
                    {error && (
                        <p className="incentive-error" role="alert">
                            {error}
                        </p>
                    )}
                </div>
            </DBModal.Content>
            <DBModal.Footer>
                <DBButton
                    type="secondary"
                    caption={t("cancel")}
                    onClick={onHide}
                />
                <DBButton
                    caption={t("incentives.purchase.confirm")}
                    loading={loading}
                    onClick={purchase}
                />
            </DBModal.Footer>
        </DBModal>
    );
}
